import dayjs from '@/lib/dayjs'
import { CartSource } from '@/lib/enums/CartSource'
import type { Address } from '@/lib/services/addresses/addresses.types'
import type {
  OrderSourceLink,
  SalesOrderCartSource,
} from '@/lib/services/sales-orders/sales-orders.types'
import { readProperty } from '@/lib/utils'

type MapsAddress = Pick<
  Address,
  | 'line1'
  | 'line2'
  | 'city'
  | 'state'
  | 'postal_code'
  | 'country'
  | 'lat'
  | 'lng'
  | 'google_place_id'
>

export function getSalesOrderSourceSummary(
  source: SalesOrderCartSource | null,
  createdAt: string | null,
  sourceLink: Pick<OrderSourceLink, 'source_system' | 'metadata'> | null = null
): string {
  const createdAtText = createdAt ? dayjs(createdAt).format('MMM D, YYYY [at] h:mm A') : null
  let actionText = 'Created'

  if (sourceLink?.source_system === 'condolences') {
    actionText = 'Imported from Condolences'
  } else if (sourceLink?.source_system === 'tukios_flowers_subscription') {
    actionText = 'Created from subscription'
  } else if (
    sourceLink?.source_system === 'fsn_network' &&
    readProperty(sourceLink.metadata, 'direction') === 'incoming'
  ) {
    actionText = 'Imported from Flower Shop Network'
  } else if (sourceLink?.source_system === 'fsn_network') {
    actionText = 'Sent through Flower Shop Network'
  } else if (source === CartSource.Web.Value) {
    actionText = 'Placed online'
  } else if (source === CartSource.POS.Value) {
    actionText = 'Created in store'
  }

  return createdAtText ? `${actionText} on ${createdAtText}` : actionText
}

export function buildDeliveryAddressMapsUrl(
  deliveryAddress: MapsAddress | null | undefined
): string | null {
  if (!deliveryAddress) return null

  const placeId = deliveryAddress?.google_place_id || null

  const hasCoords =
    deliveryAddress?.lat !== null &&
    deliveryAddress?.lat !== undefined &&
    deliveryAddress?.lng !== null &&
    deliveryAddress?.lng !== undefined

  const query = [
    deliveryAddress.line1,
    deliveryAddress.line2,
    deliveryAddress.city,
    deliveryAddress.state,
    deliveryAddress.postal_code,
    deliveryAddress.country,
  ]
    .filter(Boolean)
    .join(', ')
    .trim()

  const hasQuery = Boolean(query)

  if (placeId && hasQuery) {
    return `https://www.google.com/maps/search/?api=1&query_place_id=${encodeURIComponent(
      placeId
    )}&query=${encodeURIComponent(query)}`
  }

  if (hasQuery) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`
  }

  if (hasCoords) {
    return `https://www.google.com/maps/search/?api=1&query=${deliveryAddress.lat},${deliveryAddress.lng}`
  }

  return null
}
